import { isStaffTraffic } from '@/utils/staffTraffic'
import {
  IDLE_TIMEOUT_MS,
  PRESENCE_HEARTBEAT_MS,
  SITE_VISIT_DAY_KEY,
  cartHasItems,
  isCheckoutRoute,
  isExcludedVisitor,
  parisDayKey,
  shouldSendPresence,
  shouldSendVisit,
} from '@/utils/sitePresence'

const safely = fn => {
  try {
    return fn()
  } catch {
    return null
  }
}

export default ({ app, store, $axios }) => {
  if (!process.client) return

  let lastPresence = null
  let sentVisitDay = null
  let visitInFlight = false

  const excluded = () =>
    isExcludedVisitor({
      staff: isStaffTraffic(app.$auth),
      adminLoggedInUser: app.$nuxtCookies?.get?.('admin_logged_in_user'),
    })

  const visible = () => document.visibilityState !== 'hidden'

  const whenIdle = fn => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: IDLE_TIMEOUT_MS })
    } else {
      setTimeout(fn, 1000)
    }
  }

  const sendVisit = () => {
    if (visitInFlight || excluded()) return

    const today = parisDayKey(new Date())
    const storedDay = safely(() =>
      window.localStorage.getItem(SITE_VISIT_DAY_KEY)
    )
    if (
      !shouldSendVisit(sentVisitDay, today) ||
      !shouldSendVisit(storedDay, today)
    ) {
      return
    }

    visitInFlight = true
    $axios
      .$post('/site-visits', { touch_screen: navigator.maxTouchPoints > 1 })
      .then(() => {
        sentVisitDay = today
        safely(() => window.localStorage.setItem(SITE_VISIT_DAY_KEY, today))
      })
      .catch(() => {})
      .finally(() => {
        visitInFlight = false
      })
  }

  const sendPresence = () => {
    if (!visible() || excluded()) return

    const state = {
      cart: cartHasItems(store.state.cart),
      checkout: isCheckoutRoute(app.router.currentRoute?.name),
    }
    const loggedIn = Boolean(app.$auth?.loggedIn)
    const now = Date.now()
    if (!shouldSendPresence(lastPresence, { ...state, loggedIn }, now)) return

    lastPresence = { ...state, loggedIn, at: now }
    $axios.$post('/site-presence', state).catch(() => {})
  }

  const leave = () => {
    if (!lastPresence) return
    lastPresence = null
    if (typeof window.fetch !== 'function') return

    safely(() =>
      window
        .fetch(`${$axios.defaults.baseURL || ''}/site-presence/leave`, {
          method: 'POST',
          body: '{}',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          credentials: 'omit',
        })
        .catch(() => {})
    )
  }

  const afterPage = () =>
    whenIdle(() => {
      sendVisit()
      sendPresence()
    })

  const onAuthChange = () => {
    if (excluded()) {
      leave()
    } else {
      afterPage()
    }
  }

  const start = () => {
    afterPage()
    app.router.afterEach(() => afterPage())
    setInterval(sendPresence, PRESENCE_HEARTBEAT_MS)
    store.watch(state => cartHasItems(state.cart), sendPresence)
    document.addEventListener('visibilitychange', () => {
      if (visible()) {
        sendPresence()
      } else {
        leave()
      }
    })
    window.addEventListener('pagehide', leave)
    app.$auth?.$storage?.watchState?.('loggedIn', onAuthChange)
    app.$auth?.$storage?.watchState?.('user', onAuthChange)
  }

  if (typeof window.onNuxtReady === 'function') {
    window.onNuxtReady(start)
  } else {
    start()
  }
}
